/** What each AI usage column means, shown on hover of its info icon. */

const LOWER_BOUND =
  'Estimated from what passed between the user’s Claude and XT; a lower bound — the user’s own messages, Claude’s instructions and its thinking are not visible to us.';

const USERS_CLAUDE =
  'Covered by the user’s own Claude plan (Claude Desktop / claude.ai), not billed to you; this is what it would cost if XT ran the conversation itself, at API rates.';

const XT_PLATFORM =
  'The AI that XT itself runs (reviewer, richness pass, specialists, timetables, day titles, the automatic agent), paid by the XT platform.';

export const COLUMN_INFO = {
  started: 'When the session (one connection of a Claude app) started, UTC.',
  door: 'Where it was used from: Claude (consultant) — a consultant’s Claude with the organisation’s planner key; Agent portal — a travel agent signed in to the portal; Internal test — the developers’ test server; Automatic (XT) — XT’s own agent, no Claude app.',
  who: 'Who was connected, when a name is known: a portal agent and their agency, or the name a consultant typed with the planner key (not verified). Empty otherwise.',
  client:
    'The Claude app that connected, as it names itself (e.g. claude-ai, claude-code).',
  outcome:
    'How the session ended: Written; Written — points to check (written with open points); Not finished (ended without writing); Not written (refused — a write was refused); Error. Hover a badge for the raw code.',
  itineraries:
    'The references of the itineraries this session wrote. Empty: it wrote none.',
  tools: 'How many tools the user’s Claude called in the session.',
  brainstormTokens: `≈ tokens the user’s Claude read from XT and wrote to XT while planning the draft, in the Mini Planner, before the itinerary was written. ${LOWER_BOUND}`,
  brainstormUsd: `What XT platform AI cost while planning the draft (in the Mini Planner), before the itinerary was written. ${XT_PLATFORM} Exact.`,
  itineraryTokens:
    '≈ tokens after the itinerary was written (the mid-layer), or in a later session that came back only to check a written itinerary. Near zero until tools that edit a written itinerary exist.',
  itineraryUsd: `What XT platform AI cost after the itinerary was written (the mid-layer). ${XT_PLATFORM} Exact.`,
  clientTokens: `≈ all the tokens of the user’s Claude in the session, both phases. ${LOWER_BOUND}`,
  ourUsd: `${XT_PLATFORM} The session’s cost, both phases. Exact, from XT’s cost ledger.`,
  apiUsd: `The user’s Claude in this session, priced at API rates. ${USERS_CLAUDE} An estimate and a lower bound.`,
  totalUsd:
    'XT platform AI plus the user’s Claude priced at API rates: the full cost if XT ran the whole conversation itself. An estimate, because the user’s Claude part is.',

  reference: 'The itinerary’s reference in XT.',
  title: 'The itinerary’s title.',
  writtenAt: 'When the session wrote it, UTC.',
  session:
    'The session that wrote it. Shared: that session wrote several itineraries, so the figures are the whole session’s, not split between them.',
  itineraryClientTokens: `≈ the tokens of the user’s Claude in the session that wrote it. ${LOWER_BOUND}`,
  itineraryOurUsd: `${XT_PLATFORM} The cost of the session that wrote it. Exact.`,
  itineraryApiUsd: `The user’s Claude in the session that wrote it, priced at API rates. ${USERS_CLAUDE} An estimate and a lower bound.`,
  itineraryTotalUsd:
    'XT platform AI plus the user’s Claude at API rates, for the session that wrote it: the full cost if XT ran it all. An estimate.',

  tool: 'The MCP tool the user’s Claude called, or a protocol step: initialize (the handshake and the house rules) and tools/list (the tool definitions) are read whole.',
  toolCalls: 'How many times it was called in the period.',
  toolRead: '≈ tokens XT’s answers put into the user’s Claude.',
  toolWritten: '≈ tokens of arguments the user’s Claude wrote to call it.',
  toolTokens:
    '≈ read plus written: what the tool moved in and out of the user’s Claude.',

  role: 'Which XT platform AI call: coordinator (the automatic agent), reviewer, richness (the richness pass), consultant (the specialists), timetables or day titles.',
  roleCalls: 'How many model calls of that role in the period.',
  roleTokens: 'Input, output and cache tokens, exact, from XT’s cost ledger.',
  roleUsd: 'What those calls cost, exact, from XT’s cost ledger.',

  averagePerItinerary:
    'Every session that wrote an itinerary, added up, divided by the itineraries they wrote. What you pay per itinerary across all of them, so use it for budgeting; the monthly projection uses it. One unusually expensive session pulls it up.',
  medianPerItinerary:
    'The typical itinerary: the sessions that wrote one, lined up from cheapest to dearest (each at its cost divided by what it wrote), and the one in the middle. One unusually expensive session cannot pull it up. A big gap from the average means a few sessions cost far more than most.',
} as const;

export type ColumnInfoKey = keyof typeof COLUMN_INFO;
